// The words the portal uses for customer requests. The customer never sees
// these: their mail and page are written in the app (components 95 and 96).
// The starters are what the customer would read, though, because the note is
// shown to them as written, so they follow the mail copy and stay editable.

import { CustomerRequestReason, DetailField } from '../../api/customerRequests';

export interface ReasonCopy {
  reason: CustomerRequestReason;
  label: string;
  description: string;
  starter: string;
}

export const REASONS: ReasonCopy[] = [
  {
    reason: 'proof_of_address',
    label: 'Proof of address',
    description: 'A newer or different document. Checked automatically.',
    starter:
      'Your bank statement is from April, and Finanças only takes documents from the last three months. '
      + 'Any bill or statement from August or later works. From your home country is fine.',
  },
  {
    reason: 'passport_photo',
    label: 'Passport photo',
    description: 'A new photo, both pages and all four corners. Checked automatically.',
    starter:
      'Your photo shows only one page. Please lay the passport open and flat, and take the photo from a '
      + 'little further away so both pages and all four corners are in.',
  },
  {
    reason: 'id_back',
    label: 'Back of the ID card',
    description: 'Only the back. Checked against the front we have.',
    starter: 'Finanças asked to see both sides of your ID card. We have the front, only the back is missing.',
  },
  {
    reason: 'sign_again',
    label: 'Sign again',
    description: 'A new signature on the power of attorney.',
    starter: 'Please sign your power of attorney again, the way you sign your passport.',
  },
  {
    reason: 'details',
    label: 'Name or address',
    description: 'The customer confirms or corrects their details.',
    starter: 'Please check how your name and address will appear at Finanças.',
  },
  {
    reason: 'child_document',
    label: "Child's document",
    description: "The birth certificate or the child's own ID.",
    starter: 'The photo of the birth certificate is cut off. Please take it again with the whole page in.',
  },
  {
    reason: 'financas_letter',
    label: 'Finanças letter',
    description: 'Permission to open and scan the password letter.',
    starter:
      'Finanças posted your password for the Portal das Finanças to our office. '
      + 'May I open it and put a scan in your account?',
  },
  {
    reason: 'question',
    label: 'A question',
    description: 'Free answer, optional choices.',
    starter: '',
  },
];

const BY_REASON = new Map(REASONS.map(copy => [copy.reason, copy]));

export const reasonLabel = (reason: string): string =>
  BY_REASON.get(reason as CustomerRequestReason)?.label ?? reason;

export const reasonStarter = (reason: CustomerRequestReason | null): string =>
  (reason && BY_REASON.get(reason)?.starter) || '';

/** Reasons whose answer is an upload, checked by user-backend. */
export const DOCUMENT_REASONS: ReadonlySet<string> = new Set([
  'proof_of_address',
  'passport_photo',
  'id_back',
  'child_document',
]);

export const DETAIL_LABELS: Record<DetailField, string> = {
  first_name: 'First name',
  middle_name: 'Middle name',
  last_name: 'Last name',
  street: 'Street',
  postal_code: 'Postal code',
  city: 'City',
};

export const detailLabel = (field: string): string =>
  DETAIL_LABELS[field as DetailField] ?? field.replace(/_/g, ' ');

export const NOTE_MAX = 2000;
export const MAX_OPTIONS = 4;
export const OPTION_MAX = 200;
